import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Button, Typography, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { getCurrentBSDate, formatDatePart } from '../../../utils/customDate';
import useReceiptConfig from '../../../hooks/useReceiptConfig';
import { buildReceiptDocumentTitle } from '../../../utils/receiptDocumentTitle';
import { printReceiptFromTemplate } from '../../../utils/receiptPrint';

/**
 * Reusable Fee Receipt component with print preview dialog.
 * Screen preview uses styled-components.
 * Printing uses a self-contained HTML template via printReceiptFromTemplate()
 * which guarantees pixel-perfect half-A4 output on a single page.
 */
const FeeReceipt = React.forwardRef(({ open, onClose, feeData, config: configOverride, isInline = false }, ref) => {
    const { currentUser } = useSelector((state) => state.user);
    const internalPrintRef = useRef();
    const printRef = ref || internalPrintRef;

    const [savedConfig] = useReceiptConfig({
        schoolName: currentUser?.schoolName || '',
        email: currentUser?.email || ''
    });

    const formatAmount = (amtStr) => {
        const matches = amtStr.match(/(\d+(\.\d+)?)/);
        if (matches) {
            const num = parseFloat(matches[0]);
            return amtStr.replace(matches[0], num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        }
        return amtStr;
    };

    const formatCurrency = (num) =>
        (Number(num) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const receiptConfig = configOverride || savedConfig;
    const header = receiptConfig.header;
    const footer = receiptConfig.footer;

    if (!feeData) return null;

    const receiptAdvanceUsed = Number(feeData.advanceUsed) || 0;
    const lineItems = [];
    if (feeData.description) {
        feeData.description.split(',').forEach(item => {
            const trimmed = item.trim();
            const colonIdx = trimmed.indexOf(':');
            if (colonIdx > 0) {
                const label = trimmed.substring(0, colonIdx).trim();
                const amount = trimmed.substring(colonIdx + 1).trim();
                if (label && amount && !label.startsWith('-') && label.toLowerCase() !== 'advance used') {
                    lineItems.push({ label, amount });
                }
            }
        });
    }

    const baseAmount = Number(feeData.amount) || 0;
    const duesAmount = Number(feeData.duesAmount) || 0;
    const monthlyPart = Number(feeData.monthlyAmount) || 0;
    const discount = Number(feeData.discount) || 0;
    const advanceUsed = receiptAdvanceUsed;
    const cashPaid = Number(feeData.paidAmount) || 0;
    const grossAmount = Number(feeData.grossAmount) || Math.max(0, baseAmount + advanceUsed);
    const totalAmount = grossAmount;

    const thisMonthTotal = monthlyPart > 0
        ? monthlyPart
        : (lineItems.length > 0
            ? lineItems.reduce((sum, item) => {
                const match = item.amount.match(/(\d+(\.\d+)?)/);
                return sum + (match ? parseFloat(match[0]) : 0);
            }, 0)
            : Math.max(0, baseAmount - duesAmount + discount));

    const paidAmt = cashPaid + advanceUsed;
    const paymentStatus = feeData.status || 'Unpaid';
    const balanceDue = Math.max(0, totalAmount - paidAmt);

    const receiptDate = feeData.paymentDate
        ? formatDatePart(feeData.paymentDate)
        : formatDatePart(getCurrentBSDate());

    const studentDisplayName = feeData.studentName || feeData.student?.name || '---';
    const classDisplay = feeData.className || feeData.student?.sclassName?.sclassName || '---';
    const rollDisplay = feeData.rollNum || feeData.student?.rollNum || '---';
    const remarks = feeData.remarks || feeData.month || '';

    const handlePrint = () => {
        printReceiptFromTemplate({
            title: buildReceiptDocumentTitle(feeData),
            header,
            student: {
                name: studentDisplayName,
                className: classDisplay,
                rollNo: rollDisplay,
                month: feeData.month || '---'
            },
            meta: {
                billNumber: feeData.billNumber || '---',
                date: receiptDate,
                status: paymentStatus
            },
            lineItems,
            thisMonthTotal,
            duesAmount,
            discount,
            advanceUsed,
            balanceDue,
            paidAmt,
            totalAmount,
            remarks,
            footer
        });
    };

    const ReceiptContent = () => (
        <ReceiptPaper
            elevation={0}
            ref={printRef}
            className="receipt-half-page"
            style={isInline ? { boxShadow: 'none', margin: 0, padding: '12px' } : {}}
        >
            <ReceiptLayout className="receipt-half-layout">
                {/* Top row: school header + receipt title + student info */}
                <TopRow>
                    <SchoolBlock>
                        {header.logo && (
                            <LogoImg src={header.logo} alt="Logo" />
                        )}
                        <SchoolName>{header.schoolName || 'SCHOOL NAME'}</SchoolName>
                        <SchoolDetail>{header.address || 'School Address'}</SchoolDetail>
                        <SchoolDetail>
                            Tel: {header.contact || 'N/A'}
                            {header.email ? ` | Email: ${header.email}` : ''}
                        </SchoolDetail>
                        {header.panNumber && (
                            <SchoolDetail><strong>PAN No:</strong> {header.panNumber}</SchoolDetail>
                        )}
                    </SchoolBlock>

                    <StudentBlock>
                        <ReceiptTitleBox>Receipt Bill</ReceiptTitleBox>
                        <StudentField><Label>Name :</Label><Value>{studentDisplayName}</Value></StudentField>
                        <StudentField><Label>Class :</Label><Value>{classDisplay}</Value></StudentField>
                        <StudentField><Label>Roll No. :</Label><Value>{rollDisplay}</Value></StudentField>
                        <StudentField><Label>Month :</Label><Value>{feeData.month || '---'}</Value></StudentField>
                    </StudentBlock>
                </TopRow>

                {/* Main body: receipt meta + fee table + summary */}
                <MainRow className="receipt-half-main">
                    <MetaBox>
                        <MetaRow><MetaLabel>Receipt No.:</MetaLabel><MetaValue>{feeData.billNumber || '---'}</MetaValue></MetaRow>
                        <MetaRow><MetaLabel>Date:</MetaLabel><MetaValue>{receiptDate}</MetaValue></MetaRow>
                        <MetaRow><MetaLabel>Status:</MetaLabel><MetaValue>{paymentStatus}</MetaValue></MetaRow>
                    </MetaBox>

                    <TableSection>
                        <ReceiptTable>
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>S.No</th>
                                    <th>Fee Description</th>
                                    <th style={{ width: '120px', textAlign: 'right' }}>Amount (Rs.)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems.length > 0 ? lineItems.map((item, i) => (
                                    <tr key={i}>
                                        <td style={{ textAlign: 'center' }}>{i + 1}</td>
                                        <td>{item.label}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            {item.amount.toString().includes('Rs.')
                                                ? formatAmount(item.amount.toString())
                                                : formatCurrency(parseFloat(item.amount) || 0)}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td style={{ textAlign: 'center' }}>1</td>
                                        <td>{feeData.month || 'Fee Payment'}</td>
                                        <td style={{ textAlign: 'right' }}>{formatCurrency(thisMonthTotal)}</td>
                                    </tr>
                                )}
                                {/* Empty rows to fill table height like traditional receipts */}
                                {Array.from({ length: Math.max(0, 4 - lineItems.length) }).map((_, i) => (
                                    <tr key={`empty-${i}`} className="empty-row">
                                        <td>&nbsp;</td>
                                        <td>&nbsp;</td>
                                        <td>&nbsp;</td>
                                    </tr>
                                ))}
                            </tbody>
                        </ReceiptTable>
                    </TableSection>

                    <SummaryBox>
                        <SummaryRow>
                            <SummaryLabel>This month&apos;s total :</SummaryLabel>
                            <SummaryValue>{formatCurrency(thisMonthTotal)}</SummaryValue>
                        </SummaryRow>
                        {duesAmount > 0 && (
                            <SummaryRow>
                                <SummaryLabel>Previous balance :</SummaryLabel>
                                <SummaryValue>{formatCurrency(duesAmount)}</SummaryValue>
                            </SummaryRow>
                        )}
                        {discount > 0 && (
                            <SummaryRow className="discount">
                                <SummaryLabel>Discount :</SummaryLabel>
                                <SummaryValue>- {formatCurrency(discount)}</SummaryValue>
                            </SummaryRow>
                        )}
                        {advanceUsed > 0 && (
                            <SummaryRow className="discount">
                                <SummaryLabel>Advance used :</SummaryLabel>
                                <SummaryValue>- {formatCurrency(advanceUsed)}</SummaryValue>
                            </SummaryRow>
                        )}
                        <SummaryRow className="total-due">
                            <SummaryLabel>Total balance due :</SummaryLabel>
                            <SummaryValue>{formatCurrency(balanceDue)}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                            <SummaryLabel>Paid amount :</SummaryLabel>
                            <SummaryValue>{formatCurrency(paidAmt)}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                            <SummaryLabel>Bill total :</SummaryLabel>
                            <SummaryValue>{formatCurrency(totalAmount)}</SummaryValue>
                        </SummaryRow>
                    </SummaryBox>
                </MainRow>

                {/* Footer: remarks + signatures */}
                <RemarksBox>
                    <strong>Remarks :</strong> {remarks}
                </RemarksBox>

                <SignatureRow>
                    <SignatureBox align="left">
                        <SignatureLine />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#000000', mt: 0.5 }}>
                            Authorized Signature
                        </Typography>
                    </SignatureBox>
                    <SignatureBox align="right">
                        <SignatureLine />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#000000', mt: 0.5 }}>
                            Student/Parent Signature
                        </Typography>
                    </SignatureBox>
                </SignatureRow>

                {footer && (
                    <FooterNote>{footer}</FooterNote>
                )}
            </ReceiptLayout>
        </ReceiptPaper>
    );

    if (isInline) {
        return <ReceiptContent />;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ p: 1.5, fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1rem' }}>
                Receipt Preview
                <Button
                    startIcon={<PrintIcon />}
                    size="small"
                    variant="contained"
                    onClick={handlePrint}
                    sx={{ borderRadius: '6px', textTransform: 'none', fontWeight: 700 }}
                >
                    Print
                </Button>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: '#f1f5f9', overflowX: 'auto' }}>
                <Box sx={{ p: { xs: 1, md: 2 }, display: 'flex', justifyContent: 'center' }}>
                    <ReceiptContent />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 1 }}>
                <Button size="small" onClick={onClose} sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
});

export default FeeReceipt;

const ReceiptPaper = styled(Paper)`
    padding: 16px 20px;
    background: white;
    width: 100%;
    height:auto;
    max-width: 210mm;
    margin: 12px auto;
    border-radius: 4px !important;
    font-family: 'Times New Roman', Times, serif;
    color: #1a1a2e;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
    border: 1px solid #334155;

    @media screen {
        &.receipt-half-page {
            min-height: 148.5mm;
            height: auto;
        }
    }
`;

const ReceiptLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;
`;

const TopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    border-bottom: 1px solid #334155;
    padding-bottom: 8px;

    @media print {
        gap: 2mm;
        padding-bottom: 1mm;
        flex-shrink: 0;
    }
`;

const SchoolBlock = styled.div`
    flex: 1;
    min-width: 0;
`;

const LogoImg = styled.img`
    max-height: 160px;
    max-width: 320px;
    object-fit: contain;
    margin-bottom: 6px;

    @media print {
        max-height: 30mm;
        max-width: 60mm;
        margin-bottom: 0.5mm;
    }
`;

const SchoolName = styled.div`
    font-size: 2rem;
    font-weight: 900;
    color: #1e293b;
    line-height: 1.2;

    @media print {
        font-size: 26pt !important;
        line-height: 1.4 !important;
    }
`;

const SchoolDetail = styled.div`
    font-size: 1rem;
    color: #334155;
    line-height: 1.4;

    @media print {
        font-size: 14pt !important;
        line-height: 1.15 !important;
    }
`;

const StudentBlock = styled.div`
    min-width: 220px;
    border: 1px solid #334155;
    padding: 8px 12px;

    @media print {
        min-width: 58mm;
        width: 58mm;
        padding: 1.5mm 2mm;
        flex-shrink: 0;
    }
`;

const ReceiptTitleBox = styled.div`
    background: #d1d5db;
    border: 1px solid #334155;
    border-radius: 20px;
    text-align: center;
    font-weight: 800;
    font-size: 1rem;
    padding: 4px 16px;
    margin-bottom: 8px;
    letter-spacing: 0.5px;

    @media print {
        font-size: 12pt !important;
        padding: 0.5mm 2mm !important;
        margin-bottom: 1mm !important;
        border-radius: 8px !important;
        background: #d1d5db !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
`;

const StudentField = styled.div`
    display: flex;
    gap: 6px;
    font-size: 0.85rem;
    line-height: 1.6;
    border-bottom: 1px dotted #cbd5e1;
    padding: 2px 0;

    &:last-child {
        border-bottom: none;
    }

    @media print {
        font-size: 11.5pt !important;
        line-height: 1.2 !important;
        padding: 0.2mm 0 !important;
    }
`;

const Label = styled.span`
    font-weight: 700;
    white-space: nowrap;
    min-width: 70px;

    @media print {
        min-width: 14mm;
    }
`;

const Value = styled.span`
    flex: 1;
    font-weight: 600;
`;

const MainRow = styled.div`
    display: flex;
    gap: 0;
    align-items: stretch;
    min-height: 180px;
    width: 100%;

    @media print {
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
    }
`;

const MetaBox = styled.div`
    border: 1px solid #334155;
    border-right: none;
    padding: 8px;
    min-width: 110px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    @media print {
        min-width: 24mm;
        width: 24mm;
        padding: 1mm !important;
        gap: 0.5mm !important;
        flex-shrink: 0;
    }
`;

const MetaRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e2e8f0;

    &:last-child {
        border-bottom: none;
    }

    @media print {
        padding-bottom: 0.5mm !important;
        gap: 0.2mm !important;
    }
`;

const MetaLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 800;
    color: #000000;

    @media print {
        font-size: 9pt !important;
    }
`;

const MetaValue = styled.span`
    font-size: 0.85rem;
    font-weight: 700;
    color: #1e293b;

    @media print {
        font-size: 11.5pt !important;
    }
`;

const TableSection = styled.div`
    flex: 1;
    min-width: 0;
`;

const SummaryBox = styled.div`
    border: 1px solid #334155;
    border-left: none;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    @media print {
        min-width: 58mm;
        width: 58mm;
        flex-shrink: 0;
        justify-content: flex-end !important;
    }
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    border-bottom: 1px solid #334155;
    font-size: 0.8rem;
    gap: 8px;

    &:last-child {
        border-bottom: none;
    }

    &.total-due {
        background: #f8fafc;
        font-weight: 800;
    }

    @media print {
        font-size: 10pt !important;
        padding: 0.6mm 1mm !important;
        gap: 1mm !important;

        &.total-due {
            background: #f1f5f9 !important;
            -webkit-print-color-adjust: exact;
        }
    }
`;

const SummaryLabel = styled.span`
    font-weight: 600;
    color: #334155;
    white-space: nowrap;
`;

const SummaryValue = styled.span`
    font-weight: 700;
    color: #1e293b;
    text-align: right;
`;

const RemarksBox = styled.div`
    font-size: 0.85rem;
    color: #000000;
    border: 1px solid #000000;
    padding: 6px 10px;
    min-height: 36px;
    margin-top: 4px;

    @media print {
        font-size: 11.5pt !important;
        padding: 0.8mm 1.5mm !important;
        min-height: 0 !important;
        margin-top: 0.5mm !important;
        flex-shrink: 0;
    }
`;

const SignatureRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 32px;
    margin-top: 16px;
    padding-top: 8px;

    @media print {
        gap: 4mm;
        margin-top: 6mm;
        padding-top: 0;
        flex-shrink: 0;
    }
`;

const SignatureBox = styled.div`
    flex: 1;
    text-align: ${props => props.align || 'center'};
    min-width: 180px;
    max-width: 280px;

    @media print {
        min-width: 0;
        max-width: none;

        .MuiTypography-root {
            font-size: 10pt !important;
            margin-top: 1mm !important;
        }
    }
`;

const SignatureLine = styled.div`
    border-top: 1px solid #334155;
    width: 100%;
    margin-top: 28px;

    @media print {
        margin-top: 16mm;
    }
`;

const FooterNote = styled.div`
    text-align: center;
    font-size: 0.75rem;
    font-weight: 700;
    color: #000000;
    margin-top: 4px;

    @media print {
        font-size: 9.5pt !important;
        margin-top: 0.3mm !important;
        line-height: 1.15 !important;
        flex-shrink: 0;
    }
`;

const ReceiptTable = styled.table`
    width: 100%;
    height: 100%;
    border-collapse: collapse;
    border: 1px solid #334155;

    .empty-row td {
        height: 24px;
    }

    @media print {
        .empty-row {
            display: none;
        }
    }

    th, td {
        padding: 5px 8px;
        border: 1px solid #334155;
        text-align: left;
        font-size: 0.85rem;
        color: #1e293b;

        @media print {
            padding: 0.5mm 1mm !important;
            font-size: 12pt !important;
            border: 0.3pt solid #1e293b !important;
            line-height: 1.15 !important;
        }
    }

    th {
        background: #f1f5f9;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 0.75rem;
        letter-spacing: 0.03em;
        color: #334155;

        @media print {
            background: #f1f5f9 !important;
            font-size: 11pt !important;
            -webkit-print-color-adjust: exact;
        }
    }
`;
